import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building2, Plus, Search, Pencil, Trash2, Users, Loader2, Upload, FileText, X, Download, Sparkles, Check, History, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useLocation } from 'react-router-dom';
import { CompanySelect } from '@/components/CompanySelect';
import { TherapeuticAreaSelect } from '@/components/TherapeuticAreaSelect';
import { DiseaseAreaSelect } from '@/components/DiseaseAreaSelect';
import { BulkJobProgress } from '@/components/BulkJobProgress';

const API = '/api';
const COMPANY_TYPES = ['Big Pharma', 'Mid Pharma', 'CDMO'];

const AGREEMENT_STATUSES = ['Not Started', 'Sent', 'Under Review', 'Finalised', 'Signed'];
const AGREEMENT_STATUS_COLORS = {
  'Not Started': 'bg-slate-100 text-slate-500',
  'Sent': 'bg-sky-100 text-sky-700',
  'Under Review': 'bg-amber-100 text-amber-700',
  'Finalised': 'bg-blue-100 text-blue-700',
  'Signed': 'bg-emerald-100 text-emerald-700',
};

const emptyForm = {
  company_name: '', company_type: 'Big Pharma',
  headquarters_country: '',
  therapeutic_interests: [], disease_areas: {}, email: '', notes: '',
  cda_status: 'Not Started', partnership_status: 'Not Started',
  represents_name: '', represents_id: '', represents_type: '',
};

export default function CompanyProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [archivedHidden, setArchivedHidden] = useState({ count: 0, profiles: [] });
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreChecked, setRestoreChecked] = useState(new Set());
  const [restoring, setRestoring] = useState(false);
  const [pharmaContacts, setPharmaContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [annualReviewOnly, setAnnualReviewOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [researching, setResearching] = useState(false);
  const [researchProgress, setResearchProgress] = useState(null);
  const [researchingId, setResearchingId] = useState(null);
  // Deep-enrich progress state lives inside <BulkJobProgress />.
  const [deckUploading, setDeckUploading] = useState(false);
  const [pendingDeckFile, setPendingDeckFile] = useState(null);
  // Generic merge tool — detects legacy duplicate pharma profiles (e.g.
  // "Merck" + "Merck KGaA" sitting alongside the canonical "Merck/MSD" +
  // "Merck KGaA/Merck Group") and offers a one-click cleanup so the user
  // doesn't have to touch the browser console.
  const [mergeRunning, setMergeRunning] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  // When creating (not editing) a new pharma company, default to running the
  // Perplexity deep-research pass immediately so the user lands on a fully
  // populated profile (pipeline mechanisms, archetype, BD summary, citations,
  // HQ country). Hidden when editing.
  const [enrichOnCreate, setEnrichOnCreate] = useState(true);
  const [creatingWithEnrich, setCreatingWithEnrich] = useState(false);

  // Bulk pharma .docx import
  const bulkDocxInputRef = useRef(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkLastRun, setBulkLastRun] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [jobDetailsCache, setJobDetailsCache] = useState({});

  const handleBulkDocxChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(docx|pdf|pptx)$/i.test(file.name)) {
      toast.error('Please upload a .docx, .pdf, or .pptx file');
      e.target.value = '';
      return;
    }
    setBulkRunning(true);
    setBulkLastRun(null);
    const fd = new FormData();
    fd.append('file', file);
    const toastId = toast.loading(`Uploading ${file.name}...`);
    try {
      const { data: kickoff } = await axios.post(`${API}/imports/bulk-pharma-docx`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,  // 5 min — large docs can take time to upload + initial-parse
      });
      const jobId = kickoff.job_id;
      toast.message('Parsing with Claude Sonnet 4.5…', { id: toastId, description: '60–120s for ~20 pharma profiles.' });
      const start = Date.now();
      let final = null;
      while (Date.now() - start < 15 * 60_000) {
        await new Promise(r => setTimeout(r, 3000));
        let job;
        try {
          const { data } = await axios.get(`${API}/imports/bulk-pharma-docx/${jobId}`, { withCredentials: true });
          job = data;
        } catch { continue; }
        if (job.stage === 'matching' || job.stage === 'parsing') {
          const p = job.progress || 0;
          const s = job.summary || {};
          let desc;
          if (job.stage === 'parsing') {
            desc = job.parse_chunks_total
              ? `LLM parsing chunk ${job.parse_chunks_done || 0}/${job.parse_chunks_total} · ${job.parse_companies_found || 0} pharmas found`
              : 'LLM working…';
          } else {
            desc = `${s.enriched_existing || 0} enriched · ${s.created_new || 0} new so far…`;
          }
          toast.message(`${job.stage === 'parsing' ? 'Parsing' : 'Enriching'} — ${p}%`, { id: toastId, description: desc });
        } else if (job.stage === 'done') { final = job; break; }
        else if (job.stage === 'failed') { throw new Error(job.error || 'Enrichment failed'); }
      }
      if (!final) throw new Error('Enrichment timed out after 15 minutes');
      const s = final.summary || {};
      setBulkLastRun({ enriched: s.enriched_existing || 0, created: s.created_new || 0 });
      toast.success(`Done — ${s.enriched_existing || 0} enriched, ${s.created_new || 0} new pharmas, ${s.contacts_added || 0} contacts`, { id: toastId });
      fetchProfiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Bulk pharma import failed', { id: toastId });
    } finally {
      setBulkRunning(false);
      if (bulkDocxInputRef.current) bulkDocxInputRef.current.value = '';
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/imports/bulk-pharma-docx`, { withCredentials: true });
      setHistoryJobs(data?.jobs || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleJobDetails = async (jobId) => {
    if (expandedJobId === jobId) { setExpandedJobId(null); return; }
    setExpandedJobId(jobId);
    if (!jobDetailsCache[jobId]) {
      try {
        const { data } = await axios.get(`${API}/imports/bulk-pharma-docx/${jobId}`, { withCredentials: true });
        setJobDetailsCache(prev => ({ ...prev, [jobId]: data }));
      } catch (err) { toast.error('Failed to load details'); }
    }
  };

  const cancelJob = async (jobId) => {
    setCancellingId(jobId);
    try {
      await axios.post(`${API}/imports/bulk-pharma-docx/${jobId}/cancel`, {}, { withCredentials: true });
      toast.success('Job cancelled');
      fetchHistory();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Cancel failed'); }
    finally { setCancellingId(null); }
  };

  // In-Licensers unified view: support ?category=pharma (or omit for All).
  // Commercial Partners use the dedicated /regional-distributors route.
  const [categoryFilter, setCategoryFilter] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('category') || '';
  });

  const fetchProfiles = async (cat) => {
    const c = cat !== undefined ? cat : categoryFilter;
    try {
      const profUrl = c ? `${API}/company-profiles?category=${encodeURIComponent(c)}` : `${API}/company-profiles`;
      const [profRes, contactRes] = await Promise.all([
        axios.get(profUrl, { withCredentials: true }),
        axios.get(`${API}/pharma-contacts?limit=100000`, { withCredentials: true }),
      ]);
      setProfiles(profRes.data);
      setPharmaContacts(contactRes.data?.items || contactRes.data || []);
      axios.get(`${API}/company-profiles/archived-to-biotech`, { withCredentials: true })
        .then(r => setArchivedHidden(r.data || { count: 0, profiles: [] }))
        .catch(() => {});
    } catch { toast.error('Failed to load profiles'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProfiles(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [categoryFilter]);

  // Sync the in-page categoryFilter with the ?category= URL param whenever
  // the user clicks a sub-tab pill (All / Pharma / Commercial Partners).
  const _ipLocation = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(_ipLocation.search);
    const next = sp.get('category') || '';
    setCategoryFilter((prev) => (prev !== next ? next : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_ipLocation.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/company-profiles/${editing}`, form, { withCredentials: true });
        toast.success('Profile updated');
      } else {
        const res = await axios.post(`${API}/company-profiles`, form, { withCredentials: true });
        const newId = res.data?.id;
        toast.success('Profile created');
        // Auto-upload pending deck file if one was selected during add
        if (pendingDeckFile && newId) {
          try {
            const formData = new FormData();
            formData.append('file', pendingDeckFile);
            await axios.post(`${API}/company-profiles/${newId}/upload-deck`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Corporate deck uploaded');
          } catch { toast.error('Profile saved but deck upload failed'); }
        }
        // Optionally run Perplexity deep-research immediately so the user
        // doesn't have to click a second button. ~30–60s round-trip.
        if (enrichOnCreate && newId) {
          const enrichToast = toast.loading(`Researching ${form.company_name} with Perplexity…`);
          setCreatingWithEnrich(true);
          try {
            const er = await axios.post(`${API}/company-profiles/${newId}/deep-research`, {}, { withCredentials: true });
            const d = er.data || {};
            const mechCount = (d.pipeline_mechanisms || []).length;
            const taCount = (d.therapeutic_interests || []).length;
            const arche = d.company_archetype || 'unknown';
            toast.success(
              `Enrichment complete · ${taCount} TA${taCount === 1 ? '' : 's'}, ${mechCount} mechanism${mechCount === 1 ? '' : 's'}, archetype: ${arche}`,
              { id: enrichToast, duration: 8000 }
            );
          } catch (err) {
            toast.error(
              err?.response?.data?.detail || 'Perplexity enrichment failed — profile saved without deep BD intel.',
              { id: enrichToast, duration: 8000 }
            );
          } finally {
            setCreatingWithEnrich(false);
          }
        }
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      setPendingDeckFile(null);
      setEnrichOnCreate(true);
      fetchProfiles();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(detail || 'This company profile already exists');
      } else {
        toast.error('Failed to save');
      }
    }
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setForm({
      company_name: p.company_name || '',
      company_type: p.company_type || 'Big Pharma',
      headquarters_country: p.headquarters_country || '',
      therapeutic_interests: Array.isArray(p.therapeutic_interests) ? p.therapeutic_interests : [],
      disease_areas: (p.disease_areas && typeof p.disease_areas === 'object') ? p.disease_areas : {},
      email: p.email || '',
      notes: p.notes || '',
      cda_status: p.cda_status || 'Not Started',
      partnership_status: p.partnership_status || 'Not Started',
      represents_name: p.represents_name || '',
      represents_id: p.represents_id || '',
      represents_type: p.represents_type || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/company-profiles/${id}`, { withCredentials: true });
      setProfiles(prev => prev.filter(p => p.id !== id));
      toast.success('Deleted');
      setDeleteTarget(null);
    } catch { toast.error('Failed to delete'); setDeleteTarget(null); }
  };

  const handleDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!['.pdf', '.ppt', '.pptx'].includes(ext)) { toast.error('Only PDF, PPT, and PPTX files allowed'); return; }
    setDeckUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/company-profiles/${editing}/upload-deck`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Corporate deck uploaded');
      fetchProfiles();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setDeckUploading(false); e.target.value = ''; }
  };

  const handleDeleteDeck = async (filename) => {
    if (!editing) return;
    try {
      await axios.delete(`${API}/company-profiles/${editing}/delete-deck`, { data: { filename }, withCredentials: true });
      toast.success('Deck removed');
      fetchProfiles();
    } catch { toast.error('Failed to remove deck'); }
  };

  const handleResearchAll = async () => {
    setResearching(true);
    setResearchProgress(null);
    try {
      const res = await axios.post(`${API}/company-profiles/research-all`, {}, { withCredentials: true });
      if (res.data.status === 'running') {
        toast.info('Research is already in progress');
      }
      // Start polling for progress
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/company-profiles/research-status`, { withCredentials: true });
          const status = statusRes.data;
          setResearchProgress(status);

          if (!status.running) {
            clearInterval(pollInterval);
            setResearching(false);
            setResearchProgress(null);
            toast.success(`Research complete — updated ${status.updated} of ${status.total} companies`);
            await fetchProfiles();
          }
        } catch {
          clearInterval(pollInterval);
          setResearching(false);
          setResearchProgress(null);
        }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start research');
      setResearching(false);
    }
  };


  const handleResearchSingle = async (profileId) => {
    setResearchingId(profileId);
    try {
      const res = await axios.post(`${API}/company-profiles/${profileId}/research`, {}, { withCredentials: true });
      const newCount = res.data.new_count || 0;
      toast.success(newCount > 0 ? `Found ${newCount} new area${newCount > 1 ? 's' : ''}` : 'Already up to date');
      await fetchProfiles();
    } catch {
      toast.error('Research failed');
    } finally {
      setResearchingId(null);
    }
  };


  // Deep-enrich-all progress is handled by the shared <BulkJobProgress />.


  // Sync annualReviewOnly with ?filter=no-contacts-known URL param so the
  // toggle survives reload + can be shared as a bookmarkable link.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('filter') === 'no-contacts-known') setAnnualReviewOnly(true);
  }, []);

  const toggleAnnualReview = () => {
    setAnnualReviewOnly(prev => {
      const next = !prev;
      const url = new URL(window.location.href);
      if (next) url.searchParams.set('filter', 'no-contacts-known');
      else url.searchParams.delete('filter');
      window.history.replaceState({}, '', url.toString());
      return next;
    });
  };

  const annualReviewCount = profiles.filter(p => p.no_contacts_known === true || p.stub_source === 'user_known_no_contacts').length;

  const filtered = profiles.filter(p => {
    if (annualReviewOnly && !(p.no_contacts_known === true || p.stub_source === 'user_known_no_contacts')) return false;
    const s = search.toLowerCase();
    if (!s) return true;
    return (
      p.company_name?.toLowerCase().includes(s) ||
      p.company_type?.toLowerCase().includes(s) ||
      (Array.isArray(p.therapeutic_interests) ? p.therapeutic_interests : []).some(a => a.toLowerCase().includes(s))
    );
  });

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="company-profiles-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            In-Licensers
            <span className="ml-3 text-sm font-normal text-slate-500 tabular-nums">· {profiles.length.toLocaleString()} total</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Standardised Export button (top-right). Conference imports +
              CSV imports + import history moved to Data Ops (2026-02). */}
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/company-profiles/export-csv`, { withCredentials: true, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.download = 'in_licensers.csv';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch { toast.error('Failed to export'); }
            }}
            data-testid="export-csv-btn"
            className="text-sm"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <BulkJobProgress
            label={`Enrich ${profiles.length} · all categories`}
            unit="profiles"
            startUrl={`${API}/company-profiles/deep-research-all`}
            statusUrl={`${API}/company-profiles-deep-research/status`}
            retryUrl={`${API}/company-profiles/deep-research-retry-failed`}
            startPayload={{ only_missing: true }}
            onDone={() => fetchProfiles()}
            confirmMessage={'Deep Enrich All Pharma Profiles?\n\nThis will run Perplexity research on every pharma profile missing pipeline mechanisms (takes a few minutes). Idempotent — already-enriched profiles are skipped. Safe to re-run.'}
            buttonClassName="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm"
            testIdPrefix="deep-enrich-all"
          />
          <Button onClick={() => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); }} data-testid="add-profile-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Company Profile
          </Button>
          <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="pharma-import-history-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-600" /> Recent Pharma .docx Imports
                  <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto"
                    onClick={fetchHistory} disabled={historyLoading} title="Refresh"
                    data-testid="pharma-history-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${historyLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </DialogTitle>
                <DialogDescription>
                  Last 20 pharma-enrichment jobs. Click a row's "View full report" for the per-company breakdown.
                </DialogDescription>
              </DialogHeader>
              {historyLoading && historyJobs.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…</div>
              ) : historyJobs.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">No pharma import jobs yet.</div>
              ) : (
                <div className="space-y-2">
                  {historyJobs.map(j => {
                    const inFlight = ['queued', 'parsing', 'matching'].includes(j.stage);
                    const stageColor = {
                      queued: 'bg-slate-100 text-slate-700',
                      parsing: 'bg-sky-100 text-sky-700',
                      matching: 'bg-violet-100 text-violet-700',
                      done: 'bg-emerald-100 text-emerald-700',
                      failed: 'bg-rose-100 text-rose-700',
                    }[j.stage] || 'bg-slate-100 text-slate-700';
                    const s = j.summary || {};
                    const created = j.created_at ? new Date(j.created_at).toLocaleString() : '—';
                    return (
                      <div key={j.id} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`pharma-history-row-${j.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-xs ${stageColor}`}>{j.stage}</Badge>
                              <span className="text-sm font-medium text-slate-800 truncate" title={j.filename}>{j.filename || 'unnamed.docx'}</span>
                              <span className="text-xs text-slate-500">· {j.progress || 0}%</span>
                            </div>
                            <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3">
                              <span>by {j.user_email || '—'}</span>
                              <span>started {created}</span>
                              {j.parse_chunks_total && (
                                <span>chunks {j.parse_chunks_done || 0}/{j.parse_chunks_total} · {j.parse_companies_found || 0} parsed</span>
                              )}
                            </div>
                            {j.stage === 'done' && (
                              <div className="mt-1 text-xs text-emerald-700">
                                {s.total_parsed || 0} parsed · {s.enriched_existing || 0} enriched · {s.created_new || 0} new · {s.contacts_added || 0} contacts
                                {Array.isArray(s.failed_chunks) && s.failed_chunks.length > 0 && (
                                  <span className="ml-2 text-amber-700 font-medium">⚠ {s.failed_chunks.length} chunk(s) skipped</span>
                                )}
                              </div>
                            )}
                            {j.error && (
                              <div className="mt-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 break-words">{j.error}</div>
                            )}
                            {(j.stage === 'done' || (Array.isArray(s.failed_chunks) && s.failed_chunks.length > 0)) && (
                              <button onClick={() => toggleJobDetails(j.id)} className="mt-1 text-xs text-sky-600 hover:underline" data-testid={`pharma-toggle-report-${j.id}`}>
                                {expandedJobId === j.id ? '▲ Hide report' : '▼ View full report'}
                              </button>
                            )}
                            {expandedJobId === j.id && (() => {
                              const full = jobDetailsCache[j.id];
                              if (!full) return <div className="mt-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin inline-block mr-1" /> loading per-company breakdown…</div>;
                              const details = full?.summary?.details || [];
                              if (details.length === 0) return <div className="mt-2 text-xs text-slate-500">No per-company details recorded.</div>;
                              return (
                                <div className="mt-2 max-h-64 overflow-y-auto border border-slate-200 rounded">
                                  <table className="w-full text-xs">
                                    <thead className="bg-slate-50 sticky top-0">
                                      <tr className="text-left">
                                        <th className="px-2 py-1">Parsed</th>
                                        <th className="px-2 py-1">Action</th>
                                        <th className="px-2 py-1">Changes</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {details.map((d, di) => (
                                        <tr key={di} className="border-t border-slate-100">
                                          <td className="px-2 py-1 text-slate-800">{d.parsed_name}</td>
                                          <td className="px-2 py-1">
                                            {d.action === 'created'
                                              ? <Badge className="bg-blue-100 text-blue-700 text-[10px]">NEW</Badge>
                                              : <span><Badge className="bg-emerald-100 text-emerald-700 text-[10px]">enriched</Badge> <span className="ml-1 text-slate-600">{d.matched_name}</span></span>}
                                          </td>
                                          <td className="px-2 py-1 text-slate-500">{d.contacts_added ? `${d.contacts_added} contacts` : ''}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            })()}
                          </div>
                          {inFlight && (
                            <Button variant="outline" size="sm"
                              className="h-7 px-2 text-xs text-rose-700 border-rose-300 hover:bg-rose-50 shrink-0"
                              onClick={() => cancelJob(j.id)}
                              disabled={cancellingId === j.id}
                              data-testid={`pharma-cancel-job-${j.id}`}>
                              {cancellingId === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><XCircle className="w-3 h-3 mr-1" /> Cancel</>}
                            </Button>
                          )}
                          {j.stage === 'failed' && (
                            <Button variant="outline" size="sm"
                              className="h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50 shrink-0"
                              onClick={() => { setHistoryOpen(false); bulkDocxInputRef.current?.click(); }}
                              data-testid={`pharma-retry-job-${j.id}`}>
                              <RefreshCw className="w-3 h-3 mr-1" /> Retry
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <DialogFooter>
                <Button
                  onClick={() => setHistoryOpen(false)}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="pharma-import-history-done-btn"
                >
                  Done
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search bar + Annual Review pill removed (2026-02). Search lives in
          the global Ctrl+K bar; Annual Review filter moved to Data Ops →
          Pharma Cleanup. */}

      {/* Aug 2026 · Roche incident — profiles soft-archived by an asset
          import are invisible in this list. Surface them for review. */}
      {archivedHidden.count > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5" data-testid="archived-profiles-banner">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">{archivedHidden.count} compan{archivedHidden.count === 1 ? 'y is' : 'ies are'} hidden</span> from this list — auto-archived during an asset import (e.g. Roche, Pfizer). They can be restored without losing any data.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
            data-testid="review-archived-btn"
            onClick={() => { setRestoreChecked(new Set(archivedHidden.profiles.map(p => p.id))); setRestoreDialogOpen(true); }}
          >
            Review &amp; Restore
          </Button>
        </div>
      )}

      <Dialog open={restoreDialogOpen} onOpenChange={(o) => { if (!restoring) setRestoreDialogOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="restore-archived-dialog">
          <DialogHeader>
            <DialogTitle>Hidden In-Licensers — Review &amp; Restore</DialogTitle>
            <DialogDescription>
              These pharma profiles were soft-archived when an asset import promoted them to the Out-Licensers side. Restoring makes them visible here again; their contacts, tags and outreach history were never deleted. The linked out-licenser record is kept (dual presence is legitimate).
            </DialogDescription>
          </DialogHeader>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {archivedHidden.profiles.map((p) => (
              <label key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`restore-row-${p.id}`}>
                <input
                  type="checkbox"
                  checked={restoreChecked.has(p.id)}
                  onChange={(e) => {
                    const next = new Set(restoreChecked);
                    if (e.target.checked) next.add(p.id); else next.delete(p.id);
                    setRestoreChecked(next);
                  }}
                />
                <span className="font-medium text-slate-800">{p.company_name}</span>
                {p.company_type && <Badge variant="outline" className="text-[10px]">{p.company_type}</Badge>}
                <span className="text-xs text-slate-500">{p.employee_count} contact{p.employee_count === 1 ? '' : 's'}</span>
                <span className="ml-auto text-[11px] text-slate-400">
                  {p._archived_source || 'asset import'}{p._archived_at ? ` · ${String(p._archived_at).slice(0, 10)}` : ''}
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={restoring} onClick={() => setRestoreDialogOpen(false)} data-testid="restore-cancel-btn">Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={restoring || restoreChecked.size === 0}
              data-testid="restore-confirm-btn"
              onClick={async () => {
                setRestoring(true);
                try {
                  const r = await axios.post(`${API}/company-profiles/restore-archived`,
                    { ids: Array.from(restoreChecked) }, { withCredentials: true });
                  toast.success(`${r.data.restored} compan${r.data.restored === 1 ? 'y' : 'ies'} restored to the In-Licensers list`);
                  setRestoreDialogOpen(false);
                  fetchProfiles();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
                finally { setRestoring(false); }
              }}
            >
              {restoring ? 'Restoring…' : `Restore ${restoreChecked.size} Selected`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">{profiles.length === 0 ? 'No company profiles yet' : 'No results matching your search'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          {/* Auto-detect either (a) legacy "Merck" / "Merck KGaA" still
              present OR (b) canonical groups with empty therapeutic_interests
              that need enrichment. The button adapts. */}
          {(() => {
            const f = (filtered || []);
            const byName = name => f.find(p => (p.company_name || '').trim().toLowerCase() === name);
            const hasMerck = !!byName('merck');
            const hasMerckKgaa = !!byName('merck kgaa');
            const merckMsd = byName('merck/msd');
            const merckGroup = byName('merck kgaa/merck group');
            const needsMerge = hasMerck || hasMerckKgaa;
            const msdNeedsEnrich = merckMsd && !(merckMsd.therapeutic_interests || []).length;
            const kgaaNeedsEnrich = merckGroup && !(merckGroup.therapeutic_interests || []).length;
            const needsEnrich = msdNeedsEnrich || kgaaNeedsEnrich;
            if (!needsMerge && !needsEnrich) return null;
            const bannerText = needsMerge
              ? (<><strong>Legacy duplicates detected.</strong>{' '}{hasMerck && <span>&ldquo;Merck&rdquo;</span>}{hasMerck && hasMerckKgaa && ' and '}{hasMerckKgaa && <span>&ldquo;Merck KGaA&rdquo;</span>} will be merged into Merck/MSD + Merck KGaA/Merck Group, then enriched via Perplexity. Contacts, outreach, AI mapping alerts, and the public share link auto-update.</>)
              : (<><strong>Enrichment pending.</strong>{' '}{msdNeedsEnrich && <span>Merck/MSD</span>}{msdNeedsEnrich && kgaaNeedsEnrich && ' and '}{kgaaNeedsEnrich && <span>Merck KGaA/Merck Group</span>} {((msdNeedsEnrich && kgaaNeedsEnrich) ? 'are' : 'is')} missing therapeutic interests. Click to populate via Perplexity.</>);
            const btnLabel = needsMerge ? 'Clean up & enrich' : 'Enrich now';
            const busyLabel = needsMerge ? 'Merging & enriching…' : 'Enriching…';
            return (
              <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-4" data-testid="merck-cleanup-banner">
                <div className="text-xs text-amber-900">{bannerText}</div>
                <Button
                  size="sm"
                  onClick={async () => {
                    const confirmMsg = needsMerge
                      ? 'Merge legacy Merck + Merck KGaA into Merck/MSD + Merck KGaA/Merck Group, then run Perplexity enrichment on both?\\n\\nIdempotent — safe to re-run.'
                      : 'Run Perplexity enrichment on the canonical Merck profile(s) to populate therapeutic interests, archetype, and BD summary?';
                    if (!window.confirm(confirmMsg)) return;
                    setMergeRunning(true);
                    try {
                      let pp = null;
                      if (needsMerge) {
                        const res = await axios.post(`${API}/admin/merge-pharma-profiles/apply`, {}, { withCredentials: true });
                        setMergeResult(res.data);
                        toast.success(`Merged: ${res.data.contacts_renamed} contacts, ${res.data.profiles_deleted} legacy profiles deleted. Enriching survivors…`);
                        const [ppRes, pcRes] = await Promise.all([
                          axios.get(`${API}/company-profiles`, { withCredentials: true }),
                          axios.get(`${API}/pharma-contacts?limit=100000`, { withCredentials: true }),
                        ]);
                        pp = ppRes.data || [];
                        setProfiles(pp);
                        setPharmaContacts(pcRes.data || []);
                      } else {
                        pp = profiles;
                      }
                      // Find canonical survivors that need enrichment.
                      const survivors = (pp || []).filter(p =>
                        ['merck/msd', 'merck kgaa/merck group']
                          .includes((p.company_name || '').trim().toLowerCase())
                      );
                      // If we only need enrich (not merge), restrict to those
                      // actually missing TAs so we don't burn Perplexity budget
                      // on already-enriched groups.
                      const toEnrich = needsMerge ? survivors
                        : survivors.filter(s => !(s.therapeutic_interests || []).length);
                      if (toEnrich.length === 0) {
                        toast.warning('Nothing to enrich — both canonical Merck profiles already have therapeutic interests.');
                      } else {
                        const t = toast.loading(`Enriching ${toEnrich.length} group${toEnrich.length > 1 ? 's' : ''} (therapeutic interests + BD summary)…`);
                        // Two-step per survivor: /research then /deep-research.
                        const perSurvivor = async (s) => {
                          await axios.post(`${API}/company-profiles/${s.id}/research`, {}, { withCredentials: true });
                          await axios.post(`${API}/company-profiles/${s.id}/deep-research`, {}, { withCredentials: true });
                        };
                        const results = await Promise.allSettled(toEnrich.map(perSurvivor));
                        toast.dismiss(t);
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        const failed = results.length - ok;
                        if (failed === 0) {
                          toast.success(`Enriched ${ok} group${ok > 1 ? 's' : ''}.`);
                        } else {
                          toast.error(`Enriched ${ok}/${results.length}. ${failed} failed — open the profile and click Enrich to retry.`);
                        }
                        const ppAfter = await axios.get(`${API}/company-profiles`, { withCredentials: true });
                        setProfiles(ppAfter.data || []);
                      }
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Operation failed');
                    } finally {
                      setMergeRunning(false);
                    }
                  }}
                  disabled={mergeRunning}
                  className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                  data-testid="merck-cleanup-btn"
                >
                  {mergeRunning ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {busyLabel}</> : btnLabel}
                </Button>
              </div>
            );
          })()}
          {mergeResult && (
            <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-[11px] text-emerald-800 flex items-center justify-between" data-testid="merck-cleanup-result">
              <span>
                ✓ Merged: {mergeResult.contacts_renamed} contacts renamed · {mergeResult.profile_id_refs_restamped} profile-id refs restamped · {mergeResult.profiles_deleted} legacy profiles deleted
              </span>
              <button onClick={() => setMergeResult(null)} className="text-emerald-600 hover:text-emerald-900">×</button>
            </div>
          )}
          <Table className="table-fixed w-full" data-testid="profiles-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[18%]">Company</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[8%]">Type</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[8%]">Contacts</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[32%]">Therapeutic Interests</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[10%]">CDA</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[12%]">Partnership</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[12%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow key={p.id} className="hover:bg-slate-50/50" data-testid={`profile-row-${p.id}`}>
                  <TableCell>
                    <a href={`/company-profile/${p.id}`} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-slate-900 hover:text-violet-700 hover:underline cursor-pointer" data-testid={`link-profile-${p.id}`}>{p.company_name}</a>
                    {p.represents_name && (
                      <div className="text-[10px] text-violet-700 mt-0.5 flex items-center gap-1" data-testid={`represents-badge-${p.id}`} title={`This firm acts on behalf of ${p.represents_name}`}>
                        <span className="inline-block w-1 h-1 rounded-full bg-violet-500" />
                        Represents <span className="font-medium">{p.represents_name}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{p.company_type}</Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const count = pharmaContacts.filter(c => (c.company_name || '').toLowerCase() === (p.company_name || '').toLowerCase()).length;
                      return count > 0 ? (
                        <div className="flex items-center gap-1.5 text-xs text-slate-700">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-medium">{count}</span>
                        </div>
                      ) : <span className="text-xs text-slate-400">0</span>;
                    })()}
                  </TableCell>
                  <TableCell className="align-top">
                    {(() => {
                      const tas = Array.isArray(p.therapeutic_interests) ? p.therapeutic_interests : [];
                      if (!tas.length) return <span className="text-xs text-slate-400">—</span>;
                      const MAX = 6;
                      const visible = tas.slice(0, MAX);
                      const hidden = tas.length - MAX;
                      return (
                        <div
                          className="flex flex-wrap gap-1 max-w-full"
                          title={tas.join(' · ')}
                        >
                          {visible.map((a, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px] font-normal max-w-[180px] truncate inline-block" title={a}>{a}</Badge>
                          ))}
                          {hidden > 0 && (
                            <Badge variant="outline" className="text-[10px] font-normal text-slate-500">+{hidden} more</Badge>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${AGREEMENT_STATUS_COLORS[p.cda_status || 'Not Started'] || 'bg-slate-100 text-slate-600'}`}>
                      {p.cda_status || 'Not Started'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${AGREEMENT_STATUS_COLORS[p.partnership_status || 'Not Started'] || 'bg-slate-100 text-slate-600'}`}>
                      {p.partnership_status || 'Not Started'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleResearchSingle(p.id)} disabled={researchingId === p.id} className="text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50" title="Research interests" data-testid={`research-profile-${p.id}`}>
                        {researchingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(p)} data-testid={`edit-profile-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-profile-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm({ ...emptyForm }); setPendingDeckFile(null); setEnrichOnCreate(true); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editing ? 'Edit Pharma Company' : 'Add Pharma Company'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Company Name</Label>
              {editing ? (
                <Input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} data-testid="input-profile-company" />
              ) : (
                <CompanySelect value={form.company_name} onChange={v => setForm(p => ({ ...p, company_name: v }))} testId="input-profile-company" />
              )}
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.company_type} onValueChange={v => setForm(p => ({ ...p, company_type: v }))}>
                <SelectTrigger data-testid="input-profile-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPANY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                Headquarters Country
                <span className="text-xs font-normal text-slate-400">— used as a hint for Perplexity research</span>
              </Label>
              <Input
                value={form.headquarters_country || ''}
                onChange={e => setForm(p => ({ ...p, headquarters_country: e.target.value }))}
                placeholder="e.g. China, Switzerland, USA"
                data-testid="input-profile-hq-country"
              />
            </div>
            <div className="space-y-2">
              <Label>Therapeutic Interests</Label>
              <TherapeuticAreaSelect
                value={form.therapeutic_interests}
                onChange={(v) => {
                  const removed = (form.therapeutic_interests || []).filter(t => !v.includes(t));
                  let nextDA = { ...(form.disease_areas || {}) };
                  removed.forEach(t => { delete nextDA[t]; });
                  setForm(p => ({ ...p, therapeutic_interests: v, disease_areas: nextDA }));
                }}
                testId="input-profile-areas"
              />
              {(form.therapeutic_interests || []).length > 0 && (
                <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-slate-100">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Disease areas (optional)</p>
                  {(form.therapeutic_interests || []).map((ta) => (
                    <div key={ta} className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[11px] px-2 py-0.5 mt-0.5 shrink-0">{ta}</Badge>
                      <DiseaseAreaSelect
                        therapeuticArea={ta}
                        value={(form.disease_areas || {})[ta] || []}
                        onChange={(v) => setForm(p => ({
                          ...p,
                          disease_areas: { ...(p.disease_areas || {}), [ta]: v }
                        }))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="input-profile-notes" />
            </div>
            {/* Represents — for intermediary firms acting on behalf of a client.
                e.g. Unicorn Capital Partners represents Ozon Pharmaceuticals. */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                Represents
                <span className="text-xs font-normal text-slate-400">— if this is an intermediary acting for a client</span>
              </Label>
              <Input
                value={form.represents_name || ''}
                onChange={e => setForm(p => ({ ...p, represents_name: e.target.value }))}
                placeholder="e.g. Ozon Pharmaceuticals"
                data-testid="input-profile-represents"
              />
            </div>
            {/* Agreement Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CDA Status</Label>
                <Select value={form.cda_status || 'Not Started'} onValueChange={v => setForm(p => ({ ...p, cda_status: v }))}>
                  <SelectTrigger data-testid="select-profile-cda-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AGREEMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Partnership Agreement</Label>
                <Select value={form.partnership_status || 'Not Started'} onValueChange={v => setForm(p => ({ ...p, partnership_status: v }))}>
                  <SelectTrigger data-testid="select-profile-partnership-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AGREEMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Corporate Presentation Upload */}
            <div className="space-y-2">
              <Label>Corporate Presentation</Label>
              {editing && (() => {
                const profile = profiles.find(p => p.id === editing);
                const decks = profile?.corporate_decks || [];
                return (
                  <div className="space-y-2">
                    {decks.map((deck, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                        <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                        <span className="text-xs text-slate-700 flex-1 truncate">{deck.filename}</span>
                        <button onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/company-profiles/${profile.id}/download-deck/${encodeURIComponent(deck.filename)}`, { responseType: 'blob', withCredentials: true });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = deck.filename;
                            link.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error('Failed to download file'); }
                        }} className="text-[10px] text-sky-600 hover:underline shrink-0 cursor-pointer">Download</button>
                        <button type="button" onClick={() => handleDeleteDeck(deck.filename)} className="text-slate-400 hover:text-rose-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}
              {!editing && pendingDeckFile && (
                <div className="flex items-center gap-2 bg-violet-50 rounded-lg p-2.5 border border-violet-200">
                  <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                  <span className="text-xs text-violet-700 flex-1 truncate">{pendingDeckFile.name}</span>
                  <button type="button" onClick={() => setPendingDeckFile(null)} className="text-slate-400 hover:text-rose-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              {editing ? (
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg p-3 cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-colors">
                  <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={handleDeckUpload} disabled={deckUploading} data-testid="deck-upload-input" />
                  {deckUploading ? <Loader2 className="w-4 h-4 animate-spin text-violet-500" /> : <Upload className="w-4 h-4 text-slate-400" />}
                  <span className="text-xs text-slate-500">{deckUploading ? 'Uploading...' : 'Upload PDF, PPT, or PPTX'}</span>
                </label>
              ) : (
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg p-3 cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-colors">
                  <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const ext = '.' + file.name.split('.').pop().toLowerCase();
                    if (!['.pdf', '.ppt', '.pptx'].includes(ext)) { toast.error('Only PDF, PPT, and PPTX files allowed'); return; }
                    setPendingDeckFile(file);
                  }} data-testid="deck-upload-input-add" />
                  <Upload className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">{pendingDeckFile ? 'Change file' : 'Upload PDF, PPT, or PPTX'}</span>
                </label>
              )}
            </div>
            {!editing && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    checked={enrichOnCreate}
                    onChange={e => setEnrichOnCreate(e.target.checked)}
                    data-testid="enrich-on-create-checkbox"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                      Run Perplexity deep-research after creating
                    </div>
                    <p className="text-xs text-emerald-700/80 mt-0.5">
                      Researches the company on the live web and auto-fills pipeline mechanisms,
                      BD focus stages, company archetype, and a recent-BD summary. Takes ~30–60s.
                    </p>
                  </div>
                </label>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={creatingWithEnrich}>Cancel</Button>
              <Button type="submit" data-testid="save-profile-btn" disabled={creatingWithEnrich}>
                {creatingWithEnrich ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Researching…</>
                ) : (
                  editing ? 'Update' : (enrichOnCreate ? 'Save & Enrich' : 'Save')
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Pharma Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the profile for <span className="font-semibold text-slate-700">{deleteTarget?.company_name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget?.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
